import { useEffect, useMemo, useState } from 'react';
import {
  Bar, CartesianGrid, Cell, ComposedChart, Line, ReferenceArea, ReferenceLine, ResponsiveContainer, Tooltip, XAxis, YAxis,
} from 'recharts';
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle } from '../ui/dialog';
import { api } from '../../services/api';
import { fmtYmd, type AiIhsgTs, type AiTsSplit } from '../../utils/accdistAi';

// The IHSG time-series study (backend/ai/accdist/ihsg_ts.py), for developers: the flow from foreign and domestic flows
// to tomorrow's IHSG, the data with its train / val / test parts, how every model scored, the rolling refits and the
// coefficients. Opened from the "IHSG prediksi besok" header of AI Monitoring.

const SPLITS: [AiTsSplit, string, string][] = [['train', 'Train', '#60a5fa'], ['val', 'Val', '#f59e0b'], ['test', 'Test', '#c084fc']];
const C = { ihsg: '#e5e7eb', up: '#22c55e', down: '#ef4444', dom: '#38bdf8', exp: '#f59e0b', win: '#c084fc', var: '#94a3b8' };
const AXIS = { fill: '#9ca3af', fontSize: 10 };
const TIP = { backgroundColor: '#11151d', border: '1px solid rgba(255,255,255,0.15)', borderRadius: 8, fontSize: 11 };
const ROLL_ACC_DAYS = 20;

const pct = (x: number | null | undefined) => (x == null ? '–' : `${Math.round(x * 100)}%`);
const fmt = (x: number | null | undefined, d = 1) =>
  x == null ? '–' : x.toLocaleString('id-ID', { minimumFractionDigits: d, maximumFractionDigits: d });
const signed = (x: number | null | undefined, d = 2) => (x == null ? '–' : `${x > 0 ? '+' : ''}${fmt(x, d)}`);
const short = (d: string) => fmtYmd(d).replace(/ \d{4}$/, '');

const Section = ({ title, note, children }: { title: string; note?: string; children: React.ReactNode }) => (
  <section className="mt-5">
    <h4 className="text-[13px] font-semibold text-white">{title}</h4>
    {note ? <p className="mt-0.5 text-[11px] text-gray-400">{note}</p> : null}
    <div className="mt-2">{children}</div>
  </section>
);

const Box = ({ title, lines, accent, foot }: { title: string; lines: [string, string, string?][]; accent: string; foot?: string }) => (
  <div className="flex-1 min-w-[160px] rounded-lg border bg-white/[0.03] p-2" style={{ borderColor: accent }}>
    <div className="text-[11px] font-semibold" style={{ color: accent }}>{title}</div>
    {lines.map(([k, v, cls]) => (
      <div key={k} className="mt-0.5 flex justify-between gap-2 text-[11px]">
        <span className="text-gray-400">{k}</span><span className={cls ?? 'text-gray-100'}>{v}</span>
      </div>
    ))}
    {foot ? <div className="mt-1 text-[10px] leading-snug text-gray-500">{foot}</div> : null}
  </div>
);
const Arrow = () => <div className="self-center px-1 text-gray-500">→</div>;

export function AiIhsgTsDialog({ open, onOpenChange }: { open: boolean; onOpenChange: (open: boolean) => void }) {
  const [data, setData] = useState<AiIhsgTs | null>(null);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    if (!open || data) return;
    api.getAccDistAiIhsgTs().then(r => {
      if (r.success && r.data) setData(r.data);
      else setError(r.error || 'Gagal memuat studi IHSG');
    });
  }, [open, data]);

  const series = useMemo(() => !data ? [] : data.series.dates.map((d, i) => ({
    d, close: data.series.close[i], r: data.series.r[i], F: data.series.F[i], D: data.series.D[i],
  })), [data]);
  const rolling = useMemo(() => {
    if (!data) return [];
    const ro = data.rolling;
    const hit = (pred: (number | null)[], i: number) => (pred[i] == null || ro.r[i] == null ? null : (pred[i]! > 0) === (ro.r[i]! > 0));
    return ro.dates.map((d, i) => {
      const lastN = (pred: (number | null)[]) => {
        const xs = Array.from({ length: Math.min(ROLL_ACC_DAYS, i + 1) }, (_, k) => hit(pred, i - k)).filter(x => x != null) as boolean[];
        return i + 1 < ROLL_ACC_DAYS ? null : xs.filter(Boolean).length / xs.length;
      };
      return {
        d, r: ro.r[i], exp: ro.expanding.r_hat[i], win: ro.window.r_hat[i], var: ro.expanding.var_hat[i],
        F: ro.F[i], Fexp: ro.expanding.F_hat[i], D: ro.D[i], Dexp: ro.expanding.D_hat[i],
        accExp: lastN(ro.expanding.r_hat), accWin: lastN(ro.window.r_hat),
      };
    });
  }, [data]);

  const last = series[series.length - 1];
  const n = data?.next;
  const bands = data ? SPLITS.map(([k, label, color]) => ({ k, label, color, x1: data.split[k][0], x2: data.split[k][1] })) : [];

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-5xl max-h-[88vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle>Studi time series IHSG: asing &amp; domestik → IHSG besok</DialogTitle>
          <DialogDescription>
            {data
              ? `Data harian pasar reguler ${fmtYmd(data.series.dates[0] ?? '')} – ${fmtYmd(data.series.dates[data.series.dates.length - 1] ?? '')} (${data.series.dates.length} hari) · dihitung ${data.generated_at.replace('T', ' ').replace(/\+07:00$/, ' WIB')}`
              : error ?? 'Memuat…'}
          </DialogDescription>
        </DialogHeader>
        {!data ? null : (
          <div className="text-[12px] text-gray-200">
            <Section title="Alur" note={`Model memakai data ${data.p === 1 ? 'hari ini saja' : `${data.p} hari terakhir`} (jumlah hari dipilih di bagian val) dengan regresi linear biasa. M = Rp miliar, T = Rp triliun.`}>
              <div className="flex flex-wrap items-stretch gap-1">
                <Box title={`Data hari ini · ${last ? short(last.d) : ''}`} accent={C.ihsg} lines={[
                  ['IHSG', `${signed(last?.r)}%`, (last?.r ?? 0) >= 0 ? 'text-[#22c55e]' : 'text-[#ef4444]'],
                  ['Net beli asing', `${signed(last?.F, 0)} M`, (last?.F ?? 0) >= 0 ? 'text-[#22c55e]' : 'text-[#ef4444]'],
                  ['Transaksi domestik', `${fmt(last?.D, 2)} T`],
                ]} />
                <Arrow />
                <Box title="Tahap 1 · perkiraan arus besok" accent={C.dom}
                     foot={`Dihitung dari IHSG, net beli asing dan transaksi domestik ${data.p === 1 ? 'hari ini' : `${data.p} hari terakhir`}`} lines={[
                  ['Net beli asing', `${signed(n?.F_hat, 0)} M`, (n?.F_hat ?? 0) >= 0 ? 'text-[#22c55e]' : 'text-[#ef4444]'],
                  ['Transaksi domestik', `${fmt(n?.D_hat, 2)} T`],
                ]} />
                <Arrow />
                <Box title="Tahap 2 · perkiraan IHSG besok" accent={C.exp}
                     foot="Dihitung dari IHSG hari ini, perkiraan net beli asing besok, dan perkiraan naik/turunnya transaksi domestik besok" lines={[
                  ['IHSG besok', `${signed(n?.r_hat)}%`, (n?.r_hat ?? 0) >= 0 ? 'text-[#22c55e]' : 'text-[#ef4444]'],
                ]} />
                <Arrow />
                <Box title="Hasil" accent={!n ? C.var : n.r_hat > 0 ? C.up : C.down} lines={!n ? [['Arah', '–'], ['', 'hari terakhir tidak berurutan', 'text-gray-400']] : [
                  ['Arah IHSG besok', n.r_hat > 0 ? 'Naik' : 'Turun', n.r_hat > 0 ? 'text-[#22c55e]' : 'text-[#ef4444]'],
                  ['Peluang naik', pct(n.p_up)],
                ]} />
              </div>
              <p className="mt-2 text-[11px] text-gray-400">
                Net beli domestik selalu sama dengan minus net beli asing (tiap transaksi ada pembeli dan penjual), jadi domestik
                diukur dari nilai transaksinya: (beli + jual domestik) / 2.
              </p>
            </Section>

            <Section title="Data" note="IHSG close, net beli asing (Rp miliar) dan nilai transaksi domestik (Rp triliun), dengan bagian train / val / test (tanggal yang ditebak).">
              {[
                { key: 'close', h: 150, label: 'IHSG', el: <Line dataKey="close" stroke={C.ihsg} dot={false} strokeWidth={1.5} isAnimationActive={false} name="IHSG" /> },
                {
                  key: 'F', h: 110, label: 'Asing net (M)', el: (
                    <Bar dataKey="F" name="Asing net (M)" isAnimationActive={false}>
                      {series.map(s => <Cell key={s.d} fill={(s.F ?? 0) >= 0 ? C.up : C.down} />)}
                    </Bar>
                  ),
                },
                { key: 'D', h: 110, label: 'Domestik (T)', el: <Line dataKey="D" stroke={C.dom} dot={false} strokeWidth={1.5} isAnimationActive={false} name="Domestik (T)" /> },
              ].map(c => (
                <div key={c.key} className="relative">
                  <span className="absolute left-12 top-0 z-10 text-[10px] text-gray-400">{c.label}</span>
                  <ResponsiveContainer width="100%" height={c.h}>
                    <ComposedChart data={series} syncId="ihsgts-data" margin={{ top: 12, right: 8, bottom: 0, left: 0 }}>
                      <CartesianGrid stroke="rgba(255,255,255,0.06)" vertical={false} />
                      {bands.map(b => <ReferenceArea key={b.k} x1={b.x1} x2={b.x2} fill={b.color} fillOpacity={0.08} />)}
                      <XAxis dataKey="d" tickFormatter={short} tick={AXIS} minTickGap={40} hide={c.key !== 'D'} />
                      <YAxis tick={AXIS} width={48} domain={['auto', 'auto']} />
                      {c.key === 'F' ? <ReferenceLine y={0} stroke="rgba(255,255,255,0.25)" /> : null}
                      <Tooltip contentStyle={TIP} labelFormatter={l => fmtYmd(String(l))} formatter={(v: any) => fmt(Number(v), c.key === 'D' ? 2 : c.key === 'F' ? 0 : 2)} />
                      {c.el}
                    </ComposedChart>
                  </ResponsiveContainer>
                </div>
              ))}
              <div className="mt-1 flex gap-4 text-[11px]">
                {bands.map(b => (
                  <span key={b.k}><span style={{ color: b.color }}>■</span> {b.label} {short(b.x1)} – {short(b.x2)} ({data.split[b.k][2]} hari)</span>
                ))}
              </div>
            </Section>

            <Section title="Hasil train / val / test" note="Arah tepat = arah perkiraan IHSG besok sama dengan arah IHSG yang terjadi. Val: model dilatih di train. Test: dilatih di train + val. Dengan ±50 hari, ±7 poin masih kebetulan.">
              <table className="w-full text-[11px] border-separate border-spacing-0">
                <thead className="text-gray-400">
                  <tr>
                    <th className="text-left font-normal py-1">Model</th>
                    {SPLITS.map(([k, label]) => <th key={k} className="text-right font-normal">Arah tepat {label}</th>)}
                    {SPLITS.map(([k, label]) => <th key={`r${k}`} className="text-right font-normal">RMSE {label}</th>)}
                  </tr>
                </thead>
                <tbody>
                  {data.models.map(m => (
                    <tr key={m.key} className={`border-t border-white/10 ${m.key === 'two_stage' ? 'text-white font-semibold' : ''}`}>
                      <td className="py-1 pr-2">{m.name}</td>
                      {SPLITS.map(([k]) => <td key={k} className="text-right">{pct(m.acc[k])}</td>)}
                      {SPLITS.map(([k]) => <td key={`r${k}`} className="text-right">{fmt(m.rmse[k], 2)}</td>)}
                    </tr>
                  ))}
                </tbody>
              </table>
              <p className="mt-1 text-[10px] text-gray-500">
                {data.p === 1
                  ? 'Dua tahap dan VAR memberi angka yang sama: dengan data hari ini saja dan model linear, perkiraan asing dan domestik dibuat dari data yang sama (IHSG, asing, domestik hari ini), jadi memasukkannya ke tahap 2 setara dengan memakai data itu langsung.'
                  : `Dengan ${data.p} hari terakhir, tahap 2 memakai lebih sedikit input daripada VAR: IHSG ${data.p} hari terakhir ditambah perkiraan asing besok dan perkiraan perubahan domestik besok, sedangkan VAR memakai IHSG, asing dan domestik ${data.p} hari terakhir.`}
              </p>
              <div className="mt-3 grid gap-3 md:grid-cols-2">
                <table className="w-full text-[11px]">
                  <thead className="text-gray-400"><tr><th className="text-left font-normal">Tahap 1</th>{SPLITS.map(([k, label]) => <th key={k} className="text-right font-normal">{label}</th>)}</tr></thead>
                  <tbody>
                    {(['F', 'D'] as const).map(k => (
                      <tr key={k} className="border-t border-white/10">
                        <td className="py-1">{k === 'F' ? 'Asing: RMSE (M) model / naif · arah' : 'Domestik: RMSE (T) model / naif · arah naik-turun'}</td>
                        {SPLITS.map(([s]) => (
                          <td key={s} className="text-right">
                            {fmt(data.stage1[k].rmse[s], k === 'F' ? 0 : 2)} / {fmt(data.stage1[k].rmse_naive[s], k === 'F' ? 0 : 2)} · {pct(data.stage1[k].acc[s])}
                          </td>
                        ))}
                      </tr>
                    ))}
                  </tbody>
                </table>
                <table className="w-full text-[11px]">
                  <thead className="text-gray-400"><tr><th className="text-left font-normal">Data dipakai (dipilih di val)</th><th className="text-right font-normal">RMSE val</th><th className="text-right font-normal">Arah tepat val</th></tr></thead>
                  <tbody>
                    {data.p_scores.map(x => (
                      <tr key={x.p} className={`border-t border-white/10 ${x.p === data.p ? 'text-white font-semibold' : ''}`}>
                        <td className="py-1">{x.p === 1 ? 'Hari ini saja' : `${x.p} hari terakhir`}{x.p === data.p ? ' (dipakai)' : ''}</td>
                        <td className="text-right">{fmt(x.val_rmse, 3)}</td><td className="text-right">{pct(x.val_acc)}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
              <p className="mt-1 text-[10px] text-gray-500">Naif = besok sama dengan hari ini. Arah domestik = naik/turunnya nilai transaksi dibanding hari ini.</p>
            </Section>

            <Section title="Rolling" note={`Mulai hari pertama val: tiap hari model dilatih ulang dengan semua hari sebelumnya (expanding) atau ${data.window} hari terakhir (window), lalu menebak besok. Angka dihitung di bagian test saja (val dipakai memilih P).`}>
              <table className="w-full text-[11px] mb-2">
                <thead className="text-gray-400"><tr><th className="text-left font-normal">Skema</th><th className="text-right font-normal">Arah tepat dua tahap (test)</th><th className="text-right font-normal">Arah tepat VAR (test)</th><th className="text-right font-normal">RMSE dua tahap (test)</th></tr></thead>
                <tbody>
                  {(['expanding', 'window'] as const).map(k => (
                    <tr key={k} className="border-t border-white/10">
                      <td className="py-1">{k === 'expanding' ? 'Expanding' : `Window ${data.window} hari`}</td>
                      <td className="text-right">{pct(data.rolling[k].acc_test)}</td><td className="text-right">{pct(data.rolling[k].acc_var_test)}</td>
                      <td className="text-right">{fmt(data.rolling[k].rmse_test, 2)}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
              <div className="relative">
                <span className="absolute left-12 top-0 z-10 text-[10px] text-gray-400">IHSG: nyata (batang) vs forecast (garis), %</span>
                <ResponsiveContainer width="100%" height={170}>
                  <ComposedChart data={rolling} syncId="ihsgts-roll" margin={{ top: 12, right: 8, bottom: 0, left: 0 }}>
                    <CartesianGrid stroke="rgba(255,255,255,0.06)" vertical={false} />
                    <ReferenceArea x1={data.split.test[0]} x2={data.split.test[1]} fill={C.win} fillOpacity={0.06} />
                    <XAxis dataKey="d" tickFormatter={short} tick={AXIS} minTickGap={40} hide />
                    <YAxis tick={AXIS} width={48} />
                    <ReferenceLine y={0} stroke="rgba(255,255,255,0.25)" />
                    <Tooltip contentStyle={TIP} labelFormatter={l => fmtYmd(String(l))} formatter={(v: any) => `${signed(Number(v))}%`} />
                    <Bar dataKey="r" name="Nyata" isAnimationActive={false}>
                      {rolling.map(s => <Cell key={s.d} fill={(s.r ?? 0) >= 0 ? C.up : C.down} fillOpacity={0.55} />)}
                    </Bar>
                    <Line dataKey="exp" name="Dua tahap · expanding" stroke={C.exp} dot={false} strokeWidth={1.5} isAnimationActive={false} />
                    <Line dataKey="win" name="Dua tahap · window" stroke={C.win} dot={false} strokeWidth={1.5} isAnimationActive={false} />
                    <Line dataKey="var" name="VAR · expanding" stroke={C.var} dot={false} strokeDasharray="4 3" isAnimationActive={false} />
                  </ComposedChart>
                </ResponsiveContainer>
              </div>
              <div className="relative">
                <span className="absolute left-12 top-0 z-10 text-[10px] text-gray-400">Arah tepat {ROLL_ACC_DAYS} hari terakhir</span>
                <ResponsiveContainer width="100%" height={110}>
                  <ComposedChart data={rolling} syncId="ihsgts-roll" margin={{ top: 12, right: 8, bottom: 0, left: 0 }}>
                    <CartesianGrid stroke="rgba(255,255,255,0.06)" vertical={false} />
                    <XAxis dataKey="d" tickFormatter={short} tick={AXIS} minTickGap={40} hide />
                    <YAxis tick={AXIS} width={48} domain={[0, 1]} tickFormatter={v => pct(Number(v))} />
                    <ReferenceLine y={0.5} stroke="rgba(255,255,255,0.25)" strokeDasharray="3 3" />
                    <Tooltip contentStyle={TIP} labelFormatter={l => fmtYmd(String(l))} formatter={(v: any) => pct(Number(v))} />
                    <Line dataKey="accExp" name="Expanding" stroke={C.exp} dot={false} isAnimationActive={false} connectNulls={false} />
                    <Line dataKey="accWin" name="Window" stroke={C.win} dot={false} isAnimationActive={false} connectNulls={false} />
                  </ComposedChart>
                </ResponsiveContainer>
              </div>
              {[
                { key: 'F', label: 'Tahap 1 · asing net (M): nyata vs forecast expanding', d: 0 },
                { key: 'D', label: 'Tahap 1 · domestik (T): nyata vs forecast expanding', d: 2 },
              ].map(c => (
                <div key={c.key} className="relative">
                  <span className="absolute left-12 top-0 z-10 text-[10px] text-gray-400">{c.label}</span>
                  <ResponsiveContainer width="100%" height={c.key === 'D' ? 120 : 110}>
                    <ComposedChart data={rolling} syncId="ihsgts-roll" margin={{ top: 12, right: 8, bottom: 0, left: 0 }}>
                      <CartesianGrid stroke="rgba(255,255,255,0.06)" vertical={false} />
                      <XAxis dataKey="d" tickFormatter={short} tick={AXIS} minTickGap={40} hide={c.key !== 'D'} />
                      <YAxis tick={AXIS} width={48} domain={['auto', 'auto']} />
                      <Tooltip contentStyle={TIP} labelFormatter={l => fmtYmd(String(l))} formatter={(v: any) => fmt(Number(v), c.d)} />
                      {c.key === 'F' ? (
                        <>
                          <ReferenceLine y={0} stroke="rgba(255,255,255,0.25)" />
                          <Bar dataKey="F" name="Nyata" isAnimationActive={false}>
                            {rolling.map(s => <Cell key={s.d} fill={(s.F ?? 0) >= 0 ? C.up : C.down} fillOpacity={0.55} />)}
                          </Bar>
                          <Line dataKey="Fexp" name="Forecast" stroke={C.exp} dot={false} strokeWidth={1.5} isAnimationActive={false} />
                        </>
                      ) : (
                        <>
                          <Line dataKey="D" name="Nyata" stroke={C.dom} dot={false} strokeWidth={1.5} isAnimationActive={false} />
                          <Line dataKey="Dexp" name="Forecast" stroke={C.exp} dot={false} strokeWidth={1.5} isAnimationActive={false} />
                        </>
                      )}
                    </ComposedChart>
                  </ResponsiveContainer>
                </div>
              ))}
              <div className="mt-1 flex flex-wrap gap-4 text-[11px] text-gray-300">
                <span><span style={{ color: C.exp }}>━</span> Dua tahap · expanding</span>
                <span><span style={{ color: C.win }}>━</span> Dua tahap · window</span>
                <span><span style={{ color: C.var }}>┅</span> VAR · expanding</span>
                <span><span style={{ color: C.win }}>■</span> latar = bagian test</span>
              </div>
            </Section>

            <Section title="Koefisien" note="Besarnya pengaruh tiap input, dilatih dengan semua hari. Satuan: IHSG dalam %, asing dalam Rp miliar, domestik dalam Rp triliun. Contoh: koefisien 0,0002 untuk perkiraan net beli asing besok berarti tiap Rp 1.000 M menambah perkiraan IHSG besok 0,2%.">
              <div className="grid gap-3 md:grid-cols-3">
                {([['F', 'Tahap 1 · asing besok'], ['D', 'Tahap 1 · domestik besok'], ['r', 'Tahap 2 · IHSG besok']] as const).map(([k, title]) => (
                  <table key={k} className="w-full text-[11px]">
                    <thead className="text-gray-400"><tr><th className="text-left font-normal">{title}</th><th className="text-right font-normal">koefisien</th></tr></thead>
                    <tbody>
                      {data.coef[k].names.map((nm, i) => (
                        <tr key={nm} className="border-t border-white/10"><td className="py-0.5">{nm}</td><td className="text-right tabular-nums">{fmt(data.coef[k].values[i], 4)}</td></tr>
                      ))}
                    </tbody>
                  </table>
                ))}
              </div>
            </Section>
          </div>
        )}
      </DialogContent>
    </Dialog>
  );
}
